import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useWeb3Context } from '../../contexts/web3Context'
import {
  getAllianceJoinRequests,
  approveAllianceJoinRequest,
  declineAllianceJoinRequest,
  type AllianceJoinRequestItem,
} from '../../common/allianceApi'
import { AllianceJoinRequestModal } from './AllianceJoinRequestModal'
import { alert } from '../Alert/Alert'

/** Poll cadence for pending join requests (leader side). */
const POLL_MS = 30 * 1000

/**
 * Global watcher (mounted once in App). When the connected wallet leads an alliance and there are
 * pending join requests, it shows the leader a request modal to approve/decline each one. The modal
 * is dismissable and re-appears whenever a NEW request arrives. No-op for non-leaders.
 */
export const AllianceJoinRequestWatcher: React.FC = () => {
  const { address } = useWeb3Context()
  const [requests, setRequests] = useState<AllianceJoinRequestItem[]>([])
  const [allianceName, setAllianceName] = useState<string | undefined>(undefined)
  const [dismissed, setDismissed] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const prevCount = useRef(0)

  const refresh = useCallback(async () => {
    if (!address) {
      setRequests([])
      return
    }
    try {
      const res = await getAllianceJoinRequests(address)
      if (res.isLeader) {
        setAllianceName(res.allianceName)
        setRequests(res.requests || [])
        // A NEW request after a dismissal re-opens the modal.
        if ((res.requests?.length || 0) > prevCount.current) setDismissed(false)
        prevCount.current = res.requests?.length || 0
      } else {
        setRequests([])
        prevCount.current = 0
      }
    } catch {
      setRequests([])
    }
  }, [address])

  useEffect(() => {
    void refresh()
    if (!address) return
    const id = window.setInterval(() => void refresh(), POLL_MS)
    return () => window.clearInterval(id)
  }, [address, refresh])

  // Reset per-wallet state when the wallet changes.
  const lastAddress = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (lastAddress.current !== address) {
      lastAddress.current = address
      setDismissed(false)
      prevCount.current = 0
    }
  }, [address])

  const handleApprove = useCallback(
    async (requestId: string) => {
      if (!address) return
      setBusyId(requestId)
      try {
        await approveAllianceJoinRequest(address, requestId)
        await refresh()
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        alert.open(err?.response?.data?.message || 'Could not approve the request.')
      } finally {
        setBusyId(null)
      }
    },
    [address, refresh],
  )

  const handleDecline = useCallback(
    async (requestId: string) => {
      if (!address) return
      setBusyId(requestId)
      try {
        await declineAllianceJoinRequest(address, requestId)
        await refresh()
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        alert.open(err?.response?.data?.message || 'Could not decline the request.')
      } finally {
        setBusyId(null)
      }
    },
    [address, refresh],
  )

  const open = !!address && !dismissed && requests.length > 0

  return (
    <AllianceJoinRequestModal
      open={open}
      allianceName={allianceName}
      requests={requests}
      busyId={busyId}
      onApprove={handleApprove}
      onDecline={handleDecline}
      onClose={() => setDismissed(true)}
    />
  )
}

export default AllianceJoinRequestWatcher
